// src/adapters/jiraAdapter.js - Jira import via the integrations API.
// The signed-in user's own Jira connection (OAuth, held server-side) is used; see lib/integrations.js.
import { integrationsRequest } from "../lib/integrations";
import { activeJiraJql, isDoneJiraIssue } from '../lib/jiraPolicy';

const SAMPLE = [
  {
    key: "PROD-123",
    fields: {
      summary: "API Gateway returning 502 errors during peak traffic",
      description: "Gateway timeouts observed during high load periods, affecting checkout flow",
      priority: { name: "High" },
      labels: ["production", "api", "gateway"],
      status: { name: "Open" },
      created: "2024-01-15T10:30:00.000Z",
      updated: "2024-01-15T14:22:00.000Z"
    }
  },
  {
    key: "MOB-456",
    fields: {
      summary: "iOS app crash on user profile edit",
      description: "Users experiencing crashes when editing profile information on iOS 17.2+",
      priority: { name: "Medium" },
      labels: ["mobile", "ios", "crash"],
      status: { name: "In Progress" },
      created: "2024-01-14T09:15:00.000Z",
      updated: "2024-01-15T11:45:00.000Z"
    }
  },
  {
    key: "WEB-789",
    fields: {
      summary: "Dashboard loading performance degradation",
      description: "Analytics dashboard taking >5s to load, users reporting timeout issues",
      priority: { name: "Medium" },
      labels: ["web", "performance", "dashboard"],
      status: { name: "Open" },
      created: "2024-01-13T16:20:00.000Z",
      updated: "2024-01-15T08:30:00.000Z"
    }
  },
  {
    key: "PROD-124",
    fields: {
      summary: "Database connection pool exhaustion",
      description: "Connection pool reaching max capacity during batch jobs, causing service interruptions",
      priority: { name: "Highest" },
      labels: ["database", "production", "critical"],
      status: { name: "Open" },
      created: "2024-01-15T12:00:00.000Z",
      updated: "2024-01-15T13:15:00.000Z"
    }
  }
];

// Sample initiatives for dry run
const SAMPLE_INITIATIVES = [
  {
    key: "PROD-EPIC-001",
    fields: {
      summary: "Mobile App Performance Optimization Initiative",
      description: "Comprehensive initiative to improve mobile app performance across iOS and Android platforms, targeting 50% reduction in load times",
      priority: { name: "High" },
      labels: ["performance", "mobile", "epic"],
      status: { name: "In Progress" },
      created: "2024-01-01T10:30:00.000Z",
      updated: "2024-01-15T14:22:00.000Z",
      issuetype: { name: "Epic" },
      assignee: { displayName: "Sarah Johnson", emailAddress: "sarah.j@company.com" },
      components: [{ name: "Mobile" }, { name: "Performance" }],
      fixVersions: [{ name: "Q1 2024" }],
      customfield_10016: 5, // Story Points / Effort
      customfield_10014: "PROD", // Epic Link
      customfield_10020: "Now" // Custom field for time horizon
    }
  },
  {
    key: "WEB-EPIC-002",
    fields: {
      summary: "Customer Dashboard Redesign",
      description: "Complete redesign of customer dashboard with modern UI/UX, improved analytics, and real-time data visualization",
      priority: { name: "Medium" },
      labels: ["ux", "dashboard", "redesign"],
      status: { name: "Planned" },
      created: "2024-01-10T09:15:00.000Z",
      updated: "2024-01-15T11:45:00.000Z",
      issuetype: { name: "Feature" },
      assignee: { displayName: "Mike Chen", emailAddress: "mike.c@company.com" },
      components: [{ name: "Frontend" }, { name: "Analytics" }],
      fixVersions: [{ name: "Q2 2024" }],
      customfield_10016: 8,
      customfield_10020: "Next"
    }
  }
];

async function searchJira(jql, max) {
  const data = await integrationsRequest("/api/jira/search", { body: { jql: activeJiraJql(jql), max } });
  if (!Array.isArray(data.issues)) throw new Error("Jira search returned an unexpected response.");
  if (data.issues.length === 0) console.warn("[JIRA] Search returned 0 issues. The JQL may match nothing.");
  return { ...data, issues: data.issues.filter(issue => !isDoneJiraIssue(issue)) };
}

// Fetch JIRA initiatives (Epics, Features, Initiatives)
export async function fetchJiraInitiatives({
  jql = 'issueType in (Epic, Feature, Initiative) AND updated >= -30d ORDER BY updated DESC',
  max = 50,
  dryRun = false,
} = {}) {
  if (dryRun) return SAMPLE_INITIATIVES.slice(0, 5).map(j => normalizeJiraInitiative(j));
  const data = await searchJira(jql, max);
  return data.issues.map(j => normalizeJiraInitiative(j, data.site_url));
}

export async function fetchJiraIssues({
  jql = 'updated >= -7d ORDER BY updated DESC',
  max = 0,  // 0 = fetch all (the API paginates automatically)
  dryRun = false,
} = {}) {
  if (dryRun) return SAMPLE.slice(0, 5).map(j => normalizeJiraIssue(j));
  const data = await searchJira(jql, max);
  return data.issues.map(j => normalizeJiraIssue(j, data.site_url));
}

// Convert Atlassian Document Format (ADF) to plain text
function adfToPlainText(adf, maxLen = 600) {
  try {
    if (!adf || typeof adf !== "object") return "";
    const parts = [];
    const walk = (node) => {
      if (!node) return;
      const { type, text, content } = node;
      if (type === "text" && typeof text === "string") parts.push(text);
      else if (type === "hardBreak") parts.push("\n");
      else if (type === "listItem") {
        parts.push("• ");
        (content || []).forEach(walk);
        parts.push("\n");
      } else {
        (content || []).forEach(walk);
        if (type === "paragraph" || type === "heading") parts.push("\n");
      }
    };
    (adf.content || []).forEach(walk);
    const s = parts.join("").replace(/\s+\n/g, "\n").trim();
    return s.length > maxLen ? s.slice(0, maxLen - 1) + "…" : s;
  } catch {
    return "";
  }
}



function normalizeJiraIssue(j, siteUrl) {
  const f = j.fields || {};
  const priority = (f.priority?.name || "").toLowerCase();
  const impact =
    priority.includes("highest") ? 5 :
    priority.includes("high") ? 4 :
    priority.includes("medium") ? 3 :
    priority.includes("low") ? 2 : 3;

  // Map impact to tier INTEGER (1=tier1, 2=tier2, 3=tier3)
  const tier = impact >= 4 ? 1 : impact === 3 ? 2 : 3;

  // Jira summary is a plain string; description may be ADF or string or null
  let description = "";
  if (typeof f.description === "string") description = f.description;
  else if (f.description && typeof f.description === "object") description = adfToPlainText(f.description, 800);

  // Enhanced issue object with additional metadata
  return {
    // Don't set id - let database auto-generate UUID
    ext_key: `jira:${j.key}`,
    url: siteUrl ? `${siteUrl}/browse/${j.key}` : "",
    title: f.summary || j.key,
    summary: description || "",
    source: "JIRA",
    tier,
    status: "new",
    owner: f.assignee?.displayName || "",
    created_date: f.created || new Date().toISOString(),
    updated_date: f.updated || new Date().toISOString(),
    tags: Array.isArray(f.labels) ? f.labels : [],
    related_problem_id: "",
    impact,
    // Additional JIRA-specific fields
    jira_key: j.key,
    jira_status: f.status?.name || "",
    jira_priority: f.priority?.name || "",
    jira_project: f.project?.key || "",
    jira_components: Array.isArray(f.components) ? f.components.map(c => c.name) : [],
    jira_assignee: f.assignee?.emailAddress || ""
  };
}

// Normalize JIRA initiative (Epic/Feature) to roadmap item format
function normalizeJiraInitiative(j, siteUrl) {
  const f = j.fields || {};
  const priority = (f.priority?.name || "").toLowerCase();

  // Map JIRA priority to impact score
  const impact =
    priority.includes("highest") ? 5 :
    priority.includes("high") ? 4 :
    priority.includes("medium") ? 3 :
    priority.includes("low") ? 2 : 3;

  // Extract description
  let description = "";
  if (typeof f.description === "string") description = f.description;
  else if (f.description && typeof f.description === "object") description = adfToPlainText(f.description, 800);

  // Map JIRA status to roadmap status
  const jiraStatus = (f.status?.name || "").toLowerCase();
  let roadmapStatus = "planned";
  if (jiraStatus.includes("progress") || jiraStatus.includes("development")) {
    roadmapStatus = "in_progress";
  } else if (jiraStatus.includes("done") || jiraStatus.includes("closed") || jiraStatus.includes("resolved")) {
    roadmapStatus = "done";
  }

  // Determine time horizon from custom field or fix version
  let timeHorizon = "Next"; // Default
  if (f.customfield_10020) {
    // Direct time horizon field
    timeHorizon = f.customfield_10020;
  } else if (f.fixVersions && f.fixVersions.length > 0) {
    // Infer from fix version
    const version = f.fixVersions[0].name.toLowerCase();
    if (version.includes("current") || version.includes("sprint") || version.includes("week")) {
      timeHorizon = "Now";
    } else if (version.includes("next") || version.includes("month")) {
      timeHorizon = "Next";
    } else {
      timeHorizon = "Later";
    }
  }

  // Extract effort/story points
  const effort = f.customfield_10016 || 0; // Common story points field

  return {
    title: f.summary || j.key,
    description: description,
    time_horizon: timeHorizon,
    status: roadmapStatus,
    owner: f.assignee?.displayName || "",
    expected_impact: String(impact),
    tags: Array.isArray(f.labels) ? f.labels : [],

    // Additional metadata for tracking
    jira_key: j.key,
    jira_status: f.status?.name || "",
    jira_priority: f.priority?.name || "",
    jira_type: f.issuetype?.name || "",
    jira_project: f.project?.key || "",
    jira_components: Array.isArray(f.components) ? f.components.map(c => c.name) : [],
    jira_assignee: f.assignee?.emailAddress || "",
    jira_effort: effort,
    jira_epic_link: f.customfield_10014 || "",
    jira_fix_versions: Array.isArray(f.fixVersions) ? f.fixVersions.map(v => v.name) : [],
    created_date: f.created || new Date().toISOString(),
    updated_date: f.updated || new Date().toISOString(),

    // Source tracking
    source: "JIRA",
    external_id: j.key,
    external_url: siteUrl ? `${siteUrl}/browse/${j.key}` : ""
  };
}
